import { connect } from 'react-redux';
import { maybeGetEntityOfType } from 'app/models/entities/selectors';
import { setAttribute } from 'app/models/entities/actions';
import { EntityFields, EntityStringId, IEntityType, ExtractIEntity } from 'app/types/entity';
import React, { ReactNode, ReactElement } from 'react';
import { AppDispatch } from 'redux';
import { AppState } from 'app/store';

interface IChildParams<T> {
  setValue: (value: T) => void;
  value: T;
}

// Necessary for passing data to empty value.
export const EMPTY_VALUE_UNDEFINED = 'undefined';
export const EMPTY_VALUE_NULL = 'null';

type Base = IEntityType<any>;

interface IProps<T extends Base, F extends keyof EntityFields<ExtractIEntity<T>>> {
  children: (params: IChildParams<EntityFields<ExtractIEntity<T>>[F]>) => ReactNode;
  field: F;
  id: EntityStringId;
  type: T;
  emptyValue?: typeof EMPTY_VALUE_UNDEFINED | typeof EMPTY_VALUE_NULL;
  allowBlanks?: boolean;
}

const mapStateToProps = (state: AppState, { id, field, type }: IProps<Base, string>) => {
  const entity = maybeGetEntityOfType(state, type, id);

  if (!entity) {
    throw new Error(`Unable to find ${id} in the entities table.`);
  }

  if (!entity.has(field)) {
    throw new Error(`${entity.toJSON()} does not contain ${field}.`);
  }

  return { value: entity.get(field) };
};

const mapDispatchToProps = (dispatch: AppDispatch, {
  id,
  field: attribute,
  type,
  emptyValue,
  allowBlanks = false,
}: IProps<Base, string>) => ({
  setValue: (value: unknown) => {
    let newValue = value;
    if (!allowBlanks && typeof newValue === 'string' && newValue.trim() === '') {
      newValue = '';
    }
    if (newValue === '') {
      if (emptyValue === EMPTY_VALUE_NULL) {
        newValue = null;
      }
      if (emptyValue === EMPTY_VALUE_UNDEFINED) {
        newValue = undefined;
      }
    }

    dispatch(setAttribute({ id, type, attribute, value: newValue }));
  },
});

const EntityFieldRender = <T, >({ children, setValue, value }: IChildParams<T> & {
  children: (params: IChildParams<T>) => ReactNode,
}) => (
  <>{children({ value, setValue })}</>
);

/**
 * Helper for retrieving the value and setter value of an entity type bound to a
 * primitive value.
 */
const EntityField = connect(mapStateToProps, mapDispatchToProps)(EntityFieldRender) as (
  <T extends Base, F extends keyof EntityFields<ExtractIEntity<T>>>(props: IProps<T, F>) => ReactElement | null
);

export default EntityField;
